import React from 'react';
import { useTranslation } from 'react-i18next';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { ViewStateType } from 'components/ProductTemplates/types';
import { useDeleteCheckoutDotComPaymentCard } from 'views/Accounts/hooks/useDeleteCheckoutDotComPaymentCard';
import { CardDetailResponse } from '__generated__/graphql';
import { useUpdateDefaultCheckoutDotComCard } from 'views/Accounts/hooks/useUpdateDefaultCheckoutDotComCard';
import { logError } from 'components/LogError';
import { cache } from 'graphql/cache';

interface EditCheckoutDotComCardProps {
  card: CardDetailResponse;
  onClose: () => void;
}

export default function EditCheckoutDotComCard({ card, onClose }: EditCheckoutDotComCardProps) {
  const { t } = useTranslation();
  const [checked, setChecked] = React.useState(Boolean(card.defaultMitFlag));
  const [viewState, setViewState] = React.useState<ViewStateType>(ViewStateType.DEFAULT);
  const { updateDefaultCard } = useUpdateDefaultCheckoutDotComCard();
  const { deleteCard } = useDeleteCheckoutDotComPaymentCard();

  async function attemptToUpdateDefaultCard() {
    if (!card.instrumentId) {
      return;
    }
    try {
      const { data: updateDefaultCardResponse, errors: updateDefaultCardError } = await updateDefaultCard({
        variables: {
          defaultMitCard: {
            instrumentId: card.instrumentId,
          },
        },
      });
      if (updateDefaultCardResponse?.updateDefaultMitCard.errorMessage) {
        throw new Error(updateDefaultCardResponse?.updateDefaultMitCard.errorMessage);
      }
      if (updateDefaultCardError) {
        throw updateDefaultCardError[0];
      }

      setViewState(ViewStateType.SUCCESS);
      cache.evict({ fieldName: 'paymentGetCardDetails' });
    } catch (e) {
      logError(e as Error);
      setViewState(ViewStateType.ERROR_RESULT);
    }
  }

  async function handleConfirmDeleteCardClicked() {
    if (!card.instrumentId) {
      return;
    }
    setViewState(ViewStateType.LOADING);

    try {
      const { success } = await deleteCard(card.instrumentId);
      if (success) {
        setViewState(ViewStateType.SUCCESS);
        cache.evict({ fieldName: 'paymentGetCardDetails' });
      } else {
        setViewState(ViewStateType.ERROR_RESULT);
      }
    } catch (e) {
      setViewState(ViewStateType.ERROR_RESULT);
    }
  }

  return (
    <div className="flex flex-col flex-1 bg-gray-50 dark:bg-slate-900 pb-5 px-4 w-full relative overflow-x-hidden sm:w-[360px]">
      <div className="w-full h-full overflow-y-auto flex flex-col">
        <FeedbackTemplate onClose={onClose} onCTA={() => setViewState(ViewStateType.DEFAULT)} viewState={viewState}>
          <div className="flex flex-col bg-white dark:bg-slate-800 rounded-2xl h-full w-full p-5 shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700">
            {/* Card Info Header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100 dark:border-slate-700">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange/20 to-orange/10 dark:from-teal-500/20 dark:to-teal-500/10 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-orange dark:text-teal-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {card.scheme} •••• {card.last4}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{card.clientName}</p>
              </div>
            </div>

            {/* Make Default Checkbox */}
            <label
              htmlFor="makedefault"
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl cursor-pointer mb-4"
            >
              <input
                id="makedefault"
                type="checkbox"
                checked={checked}
                onChange={() => setChecked(!checked)}
                disabled={Boolean(card.defaultMitFlag)}
                className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 disabled:opacity-50 transition-all cursor-pointer"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{t('common:default')}</span>
              {card.defaultMitFlag && (
                <span className="ml-auto text-xs bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
                  {t('common:current', 'Current')}
                </span>
              )}
            </label>

            {/* Save Button */}
            <button
              type="button"
              className={`w-full mt-auto rounded-xl px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 ${
                Boolean(card.defaultMitFlag) || !checked
                  ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
              }`}
              onClick={attemptToUpdateDefaultCard}
              disabled={Boolean(card.defaultMitFlag) || !checked}
            >
              {t('account:payments.addCard.actions.save')}
            </button>

            {/* Delete Card Link */}
            <button
              type="button"
              onClick={handleConfirmDeleteCardClicked}
              className="mt-4 text-center text-sm text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
            >
              {t('account:payments.deleteCard')}
            </button>
          </div>
        </FeedbackTemplate>
      </div>
    </div>
  );
}
